import React, { useEffect, useRef, useState } from 'react';
import { COLOR_SCHEMES, getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  HelpIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import UcatPearsonHelpOverlay from './UcatPearsonHelpOverlay';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { isPearsonTimeRemainingWarning, tickPearsonLocalCountdown } from '../utils/pearsonTimerChrome';
import { getExamTimerSeconds } from '../utils/examTimerDuration';

/** Shared chrome for timed UCAT instruction pre-screens (header, timer, colour scheme, Help/Next). */

export const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const formatUcatExtraTimeLabel = (minutes) => {
  const total = getExamTimerSeconds(minutes, 'timed_extra');
  if (!Number.isFinite(total) || total <= 0) return null;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s > 0 ? `${m} minutes ${s} seconds` : `${m} minutes`;
};

export const useUcatInkStyles = (colorScheme) => {
  const schemeOptions = getColorSchemesForExam('UCAT');
  const scheme = schemeOptions[colorScheme] || COLOR_SCHEMES.ivori;
  const ink = scheme.text || 'var(--iv-head)';
  const divider = colorScheme !== 'ivori' ? scheme.border : 'var(--iv-line)';
  return { schemeOptions, scheme, ink, divider };
};

/** Pearson-chrome branch: TmuaPearsonShell wrapper, used only when pearsonSitting is true. */
const UcatPearsonTimedPreScreen = ({
  examName = 'UCAT',
  paperTitle = 'Practice Test',
  colorScheme = 'pearsonDefault',
  setColorScheme,
  onNext,
  onEndExam,
  durationSeconds,
  children,
  showHelp = true,
  showEndExam = false,
}) => {
  const T = TMUA_PEARSON_TOKENS;
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const [showEndExamDialog, setShowEndExamDialog] = useState(false);
  const [showHelpOverlay, setShowHelpOverlay] = useState(false);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const advancedRef = useRef(false);
  const timed = Number(durationSeconds) > 0;

  const advance = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    onNextRef.current?.();
  };

  useEffect(() => {
    if (!timed) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = tickPearsonLocalCountdown(prev, { helpOpen: false });
        if (next <= 0) {
          clearInterval(interval);
          advance();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timed]);

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExamDialog,
      nextAvailable: !showEndExamDialog,
      endExamAvailable: showEndExam && !showEndExamDialog,
    },
    {
      onNext: () => { if (!showEndExamDialog) advance(); },
      onEndExam: () => { if (showEndExam) setShowEndExamDialog(true); },
      onConfirmYes: () => { if (showEndExamDialog) onEndExam?.(); },
      onConfirmNo: () => setShowEndExamDialog(false),
    }
  );

  const timeRemainingLabel = timed ? formatCountdown(secondsLeft) : null;
  const warn = timed && isPearsonTimeRemainingWarning(secondsLeft, { enabled: true });

  return (
    <TmuaPearsonShell
      examName={examName}
      title={paperTitle}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      colorSchemeLabel={getPearsonColorSchemeDropdownLabel(examName)}
      onBackToSelection={onEndExam}
      timeRemainingLabel={timeRemainingLabel ? (
        <span style={{ color: warn ? (colorScheme === 'ivori' ? '#B5532E' : '#B45309') : 'inherit' }}>{timeRemainingLabel}</span>
      ) : null}
      footerLeft={showHelp ? (
        <TmuaPearsonFooterButton onClick={() => setShowHelpOverlay(true)} icon={<HelpIcon color={T.titleBarFg} />} underline="h">
          Help
        </TmuaPearsonFooterButton>
      ) : (showEndExam ? (
        <TmuaPearsonFooterButton onClick={() => setShowEndExamDialog(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      ) : null)}
      footerRight={(
        <TmuaPearsonFooterButton variant="chrome" onClick={advance} icon={<NextIcon color="#FFFFFF" />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div className="tmua-pearson-instruction-copy" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <div style={{ maxWidth: '920px' }}>
          {typeof children === 'function' ? children({ ink: 'var(--iv-body)', scheme: { chrome: 'var(--iv-surface)' } }) : children}
        </div>
      </div>
      {showEndExamDialog ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={() => onEndExam?.()}
          onNo={() => setShowEndExamDialog(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
      {showHelpOverlay ? (
        <UcatPearsonHelpOverlay onClose={() => setShowHelpOverlay(false)} />
      ) : null}
    </TmuaPearsonShell>
  );
};

const UcatCreamTimedPreScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'ivori',
  setColorScheme,
  onNext,
  onHelp,
  durationSeconds,
  children,
  showHelp = true,
}) => {
  const { schemeOptions, scheme, ink, divider } = useUcatInkStyles(colorScheme);
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const [showFallbackHelp, setShowFallbackHelp] = useState(false);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const advancedRef = useRef(false);

  const advance = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    onNextRef.current?.();
  };

  const handleHelp = onHelp || (() => setShowFallbackHelp(true));

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          advance();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.isComposing) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'select' || tag === 'input' || tag === 'textarea') return;
      if (event.ctrlKey || event.metaKey) return;
      const isN = event.key === 'n' || event.key === 'N';
      if (!isN) return;
      event.preventDefault();
      advance();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const footerBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: ink,
    background: scheme.chrome,
    border: `1.5px solid ${scheme.border}`,
    borderRadius: '4px',
    padding: '8px 20px',
    cursor: 'pointer',
    fontFamily: 'var(--iv-font-body)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: scheme.body,
        color: ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--iv-font-body)',
      }}
    >
      <div
        style={{
          padding: '12px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 700, color: ink }}>{paperTitle}</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 700,
            color: ink,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
          Time Remaining {formatCountdown(secondsLeft)}
        </span>
      </div>

      <div
        style={{
          padding: '8px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <select
          value={schemeOptions[colorScheme] ? colorScheme : 'ivori'}
          onChange={(e) => setColorScheme?.(e.target.value)}
          aria-label="Colour Scheme"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: ink,
            fontFamily: 'var(--iv-font-body)',
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
            minWidth: '160px',
          }}
        >
          {Object.entries(schemeOptions).map(([key, s]) => (
            <option key={key} value={key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 40px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ maxWidth: '920px' }}>{children({ ink, scheme })}</div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: showHelp ? 'space-between' : 'flex-end',
          alignItems: 'center',
          padding: '14px 28px',
          borderTop: `1px solid ${divider}`,
          background: scheme.chrome,
        }}
      >
        {showHelp && (
          <button type="button" onClick={handleHelp} style={footerBtn} aria-label="Help">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Help
          </button>
        )}
        <button type="button" onClick={advance} style={footerBtn}>
          <span>
            <span style={{ textDecoration: 'underline' }}>N</span>ext
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
      {showFallbackHelp ? (
        <UcatPearsonHelpOverlay onClose={() => setShowFallbackHelp(false)} />
      ) : null}
    </div>
  );
};

/** Dispatcher: Pearson chrome iff pearsonSitting; else today's cream chrome (unchanged for orphans/fail-closed). */
export const UcatTimedPreScreen = ({ pearsonSitting = false, ...rest }) => (
  pearsonSitting
    ? <UcatPearsonTimedPreScreen {...rest} />
    : <UcatCreamTimedPreScreen {...rest} />
);

export const ucatBodyText = (ink) => ({
  fontSize: '15px',
  lineHeight: 1.6,
  color: ink,
  margin: '0 0 14px',
  fontFamily: 'var(--iv-font-body)',
});

export const ucatSectionHeading = (ink) => ({
  fontSize: '15px',
  lineHeight: 1.6,
  color: ink,
  margin: '0 0 8px',
  fontWeight: 700,
  fontFamily: 'var(--iv-font-body)',
});

export const ucatBulletList = (ink) => ({
  margin: '0 0 16px',
  paddingLeft: '22px',
  fontSize: '15px',
  lineHeight: 1.6,
  color: ink,
  fontFamily: 'var(--iv-font-body)',
  listStyleType: 'disc',
  listStylePosition: 'outside',
});

export const ucatBulletItem = (ink) => ({
  marginBottom: '6px',
  display: 'list-item',
  listStyleType: 'disc',
  color: ink,
});

export const UcatSubtestTable = ({ ink, scheme, rows, captionWide = true }) => {
  const border = `1px solid ${ink}`;
  const th = {
    border,
    padding: '10px 14px',
    fontWeight: 700,
    color: ink,
    background: scheme.chrome,
    textAlign: 'center',
    fontSize: '14px',
  };
  const td = {
    border,
    padding: '10px 14px',
    color: ink,
    textAlign: 'center',
    fontSize: '14px',
  };
  return (
    <table
      style={{
        width: captionWide ? '100%' : 'auto',
        maxWidth: '860px',
        borderCollapse: 'collapse',
        margin: '0 0 20px',
        fontFamily: 'var(--iv-font-body)',
      }}
    >
      <thead>
        <tr>
          <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>
            Subtest
          </th>
          <th style={th} rowSpan={2}>
            Number of Questions
          </th>
          <th style={th} colSpan={2}>
            Subtest Time
          </th>
        </tr>
        <tr>
          <th style={th}>UCAT</th>
          <th style={th}>UCAT (25% Extra Time)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name}>
            <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{row.name}</td>
            <td style={td}>{row.questions}</td>
            <td style={td}>{row.ucat}</td>
            <td style={td}>{row.extra}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
